import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { Toaster } from "@/components/ui/sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import TradersPage from "@/pages/TradersPage";
import BranchesPage from "@/pages/BranchesPage";
import FundsPage from "@/pages/FundsPage";
import PnlPage from "@/pages/PnlPage";
import PerformancePage from "@/pages/PerformancePage";
import AccountsPage from "@/pages/AccountsPage";
import SettingsPage from "@/pages/SettingsPage";
import AuditPage from "@/pages/AuditPage";
import InvestorPortal from "@/pages/InvestorPortal";
import AttendancePage from "@/pages/AttendancePage";
import KycPage from "@/pages/KycPage";
import ReportsPage from "@/pages/ReportsPage";
import EmployeesPage from "@/pages/EmployeesPage";
import PermissionsPage from "@/pages/PermissionsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import NoticesPage from "@/pages/NoticesPage";
import ProfitSharingPage from "@/pages/ProfitSharingPage";
import ExpensesPage from "@/pages/ExpensesPage";
import CompanyProfitPage from "@/pages/CompanyProfitPage";
import PdfReportsPage from "@/pages/PdfReportsPage";
import ClientProfilePage from "@/pages/ClientProfilePage";
import AgreementsPage from "@/pages/AgreementsPage";
import AgreementViewPage from "@/pages/AgreementViewPage";
import AgreementSettingsPage from "@/pages/AgreementSettingsPage";
import PaymentSettingsPage from "@/pages/PaymentSettingsPage";
import FundSchemesPage from "@/pages/FundSchemesPage";
import DataExportPage from "@/pages/DataExportPage";
import TradingPage from "@/pages/TradingPage";
import FuturesTradingPage from "@/pages/FuturesTradingPage";
import AcademyAdminPage from "@/pages/AcademyAdminPage";
import StudentDetailPage from "@/pages/StudentDetailPage";
import InvoiceGeneratorPage from "@/pages/InvoiceGeneratorPage";
import HrPayrollPage from "@/pages/HrPayrollPage";
import InvestmentPlansAdminPage from "@/pages/InvestmentPlansAdminPage";
import InvestmentPlansLedgerPage from "@/pages/InvestmentPlansLedgerPage";
// StudentLogin is deprecated — /student/login now redirects to unified /login
import StudentPortal from "@/pages/StudentPortal";
import SalesPortal from "@/pages/SalesPortal";
import SalesAdminPage from "@/pages/SalesAdminPage";
import SalesPersonProfilePage from "@/pages/SalesPersonProfilePage";
import { Loader2 } from "lucide-react";

import { SplashScreen } from "@/components/common/SplashScreen";

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <SplashScreen message="Securing session…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    if (user.role === 'student') return <Navigate to="/student/portal" replace />;
    if (user.role === 'investor') return <Navigate to="/investor" replace />;
    if (user.role === 'sales_person') return <Navigate to="/sales" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AuthRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <SplashScreen message="Preparing your workspace…" />;
  if (user) {
    if (user.role === 'student') return <Navigate to="/student/portal" replace />;
    if (user.role === 'investor') return <Navigate to="/investor" replace />;
    if (user.role === 'sales_person') return <Navigate to="/sales" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return <LoginPage />;
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<AuthRedirect />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/student/login" element={<Navigate to="/login" replace />} />
          <Route path="/student/portal" element={
            <ProtectedRoute roles={['student']}>
              <StudentPortal />
            </ProtectedRoute>
          } />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/sales" element={
            <ProtectedRoute roles={['sales_person']}>
              <SalesPortal />
            </ProtectedRoute>
          } />
          <Route path="/investor" element={
            <ProtectedRoute roles={['investor', 'super_admin', 'admin']}>
              <InvestorPortal />
            </ProtectedRoute>
          } />
          <Route path="/investor/trading" element={
            <ProtectedRoute roles={['investor', 'super_admin', 'admin']}>
              <TradingPage />
            </ProtectedRoute>
          } />
          <Route path="/investor/futures" element={
            <ProtectedRoute roles={['investor', 'super_admin', 'admin']}>
              <FuturesTradingPage />
            </ProtectedRoute>
          } />
          <Route element={
            <ProtectedRoute roles={['super_admin', 'admin', 'manager', 'trader', 'office_staff', 'receptionist']}>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/branches" element={<BranchesPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/:clientId" element={<ClientProfilePage />} />
            <Route path="/traders" element={<TradersPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/funds" element={<FundsPage />} />
            <Route path="/pnl" element={<PnlPage />} />
            <Route path="/performance" element={<PerformancePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/attendance" element={<AttendancePage />} />
            <Route path="/kyc" element={<KycPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/employees" element={<EmployeesPage />} />
            <Route path="/permissions" element={<PermissionsPage />} />
            <Route path="/notices" element={<NoticesPage />} />
            <Route path="/profit-sharing" element={<ProfitSharingPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/company-profit" element={<CompanyProfitPage />} />
            <Route path="/generate-reports" element={<PdfReportsPage />} />
            <Route path="/agreements" element={<AgreementsPage />} />
            <Route path="/agreements/:agreementId" element={<AgreementViewPage />} />
            <Route path="/agreement-settings" element={<AgreementSettingsPage />} />
            <Route path="/sales-team" element={<SalesAdminPage />} />
            <Route path="/sales-team/:spId" element={<SalesPersonProfilePage />} />
            <Route path="/payment-settings" element={<PaymentSettingsPage />} />
            <Route path="/fund-schemes" element={<FundSchemesPage />} />
            <Route path="/data-export" element={<DataExportPage />} />
            <Route path="/trading" element={<TradingPage />} />
            <Route path="/futures" element={<FuturesTradingPage />} />
            <Route path="/academy" element={<AcademyAdminPage />} />
            <Route path="/academy/students/:id" element={<StudentDetailPage />} />
            <Route path="/invoices" element={<InvoiceGeneratorPage />} />
            <Route path="/hr" element={<HrPayrollPage />} />
            <Route path="/investment-plans" element={<InvestmentPlansAdminPage />} />
            <Route path="/investment-plans/ledger" element={<InvestmentPlansLedgerPage />} />
          </Route>
        </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
